import { usePageMeta } from '../lib/site';
import Legal from './Legal';

const SECTIONS = [
  {
    ps: [
      'This Privacy Policy describes how MyGolfLinks, LLC ("MyGolfLinks," "we," "us," or "our") collects, uses, and shares information about you when you visit mygolflinks.com (the "Site") or otherwise interact with us. Please read it carefully. By using the Site, you agree to the practices described in this policy.',
    ],
  },
  {
    h: '1. Information we collect',
    ps: [
      'Information you provide to us. When you request a demo, join our launch list, or contact us, you may give us your name, course or facility name, work email address, phone number, role, course type, and any information you include in your message.',
      'Information we collect automatically. When you visit the Site, we and our service providers may automatically collect information about your device and how you use the Site, such as your IP address, device and browser type, operating system, the pages you view, the links you click, and the dates and times of your visits. We collect this information using cookies and similar technologies, described below.',
      'We do not intentionally collect sensitive personal information, and we ask that you not send it to us through the Site.',
    ],
  },
  {
    h: '2. Golfer and app data (the MyGolfLinks app)',
    ps: [
      'This page covers mygolflinks.com. If you use the MyGolfLinks app, a fuller policy governs your golfer account and is presented in the app itself; you can read it any time at app.mygolflinks.com/#/privacy. In summary, for app users we collect: account and identity details (name, username, email; passwords are stored only as a salted hash and never seen by us); optional profile details; golf and performance data (rounds, hole-by-hole scores, handicap and strokes-gained stats, badges); a self-entered GHIN number and handicap index (see the next section); precise location and GPS data, which we treat as sensitive, when you use GPS features; social content you post; payment metadata only, where online payment is offered (card details go directly to Stripe and never touch our servers); and limited device and technical information. We do not collect your date of birth or age. We do not sell your personal information, and we do not use it for third-party advertising.',
    ],
  },
  {
    h: '3. Handicap, GHIN, and USGA data',
    ps: [
      'Your Handicap Index and GHIN number in MyGolfLinks are self-reported: you enter them yourself and we display them as you provide them. We do not currently connect to the USGA Golf Profile API or the GHIN service, so the handicap we show is not an official USGA/GHIN World Handicap Index; it is provisional and computed from the rounds you record. Official corrections must be made through the USGA/GHIN system, not through us.',
      'If we later integrate official USGA/GHIN data, we will do so only with your authorization, use it only to provide golf features (handicap display, round tracking, competition scoring), protect it with the safeguards described in this policy, and purge cached copies when you disconnect the integration or delete your account. We will never sell, rent, sublicense, or otherwise commercialize GHIN or USGA-sourced data, we will not use it for advertising, and we will not transfer it to data brokers.',
    ],
  },
  {
    h: '4. How we use your information',
    lis: [
      'To respond to your demo requests, questions, and other inquiries.',
      'To provide information about LinksOS, MyGolfLinks, and our products and services.',
      'To send you launch updates and other communications you have requested.',
      'To operate, maintain, secure, analyze, and improve the Site and our services.',
      'To detect, prevent, and address fraud, abuse, security incidents, and technical issues.',
      'To comply with our legal obligations and enforce our terms and agreements.',
    ],
  },
  {
    h: '5. Legal bases for processing (for individuals in the EEA and UK)',
    ps: [
      'Where the laws of the European Economic Area or the United Kingdom apply, we process your personal information on one or more of the following legal bases: your consent; the performance of a contract with you, or steps taken at your request before entering into a contract; our legitimate interests in operating and improving our business, where those interests are not overridden by your rights; and compliance with our legal obligations.',
    ],
  },
  {
    h: '6. How we share your information',
    ps: ['We do not sell your personal information. We share it only as described here:'],
    lis: [
      'Service providers. We share information with vendors who perform services on our behalf, such as form handling, payment processing (Stripe), website hosting, email delivery (Resend), and analytics. These providers may access your information only to perform services for us and are obligated to protect it.',
      'Legal and safety. We may disclose information if required by law, regulation, legal process, or governmental request, or where we believe disclosure is necessary to protect the rights, property, or safety of MyGolfLinks, our users, or others.',
      'Business transfers. If we are involved in a merger, acquisition, financing, reorganization, or sale of assets, your information may be transferred as part of that transaction.',
      'With your direction. We may share information at your request or with your consent.',
    ],
  },
  {
    h: '7. Cookies and similar technologies',
    ps: [
      'We use cookies and similar technologies to operate the Site, remember your preferences, understand how the Site is used, and improve your experience. You can usually set your browser to refuse cookies or to alert you when cookies are being sent, though some parts of the Site may not function properly without them.',
    ],
  },
  {
    h: '8. Analytics',
    ps: [
      'We may use third-party analytics services to help us understand how visitors use the Site. These services may set their own cookies and collect information such as your IP address and usage patterns. Their handling of that information is governed by their own privacy policies.',
    ],
  },
  {
    h: '9. Marketing communications',
    ps: [
      'If you have asked to hear from us, we may send you emails about products, features, and updates. You can opt out at any time by following the unsubscribe instructions in those emails or by contacting us. We may still send you non-promotional messages, such as responses to your inquiries.',
    ],
  },
  {
    h: '10. Data retention',
    ps: [
      'We retain your personal information for as long as necessary to fulfill the purposes described in this policy, including to provide our services, comply with our legal obligations, resolve disputes, and enforce our agreements. After that, we delete or de-identify it.',
    ],
  },
  {
    h: '11. Data security',
    ps: [
      'We maintain reasonable administrative, technical, and physical safeguards designed to protect your information. Payment information is processed by Stripe, a PCI-compliant payment provider, and is not stored on our servers. No method of transmission over the internet or method of electronic storage is completely secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    h: '12. Your privacy rights',
    ps: [
      'Depending on where you live, you may have rights regarding your personal information, including the rights to access, correct, update, delete, or receive a copy of your information, and to object to or restrict certain processing or withdraw your consent. To exercise these rights, contact us using the details below. We will respond as required by applicable law, and we will not discriminate against you for exercising your rights.',
    ],
  },
  {
    h: '13. California privacy rights',
    ps: [
      'If you are a California resident, you may have additional rights under the California Consumer Privacy Act, as amended, including the rights to know what personal information we collect and how we use and share it, to request deletion or correction of your personal information, and to opt out of the sale or sharing of personal information. We do not sell or share personal information as those terms are defined under California law. To exercise your rights, contact us using the details below.',
    ],
  },
  {
    h: '14. International users and data transfers',
    ps: [
      'We are based in the United States and process and store information in the United States and other countries. If you access the Site from outside the United States, you understand that your information may be transferred to and processed in countries whose data protection laws may differ from those in your country.',
    ],
  },
  {
    h: '15. Third-party links',
    ps: [
      'The Site may contain links to third-party websites and services that we do not operate or control, including our web application. This policy does not apply to those third parties, and we encourage you to review their privacy policies.',
    ],
  },
  {
    h: "16. Children's privacy",
    ps: [
      'MyGolfLinks is not directed to children under 13, and we do not knowingly collect personal information from anyone under 13. If you believe a child under 13 has provided us with personal information, please contact us and we will delete it promptly. Users between 13 and 18 should use MyGolfLinks with a parent or guardian, and only adults may make purchases. We do not sell or share the personal information of any user we know to be under 18.',
    ],
  },
  {
    h: '17. Changes to this policy',
    ps: [
      'We may update this Privacy Policy from time to time. When we do, we will revise the "Effective" date at the top of this page, and material changes will be posted here. Your continued use of the Site after an update means you accept the revised policy.',
    ],
  },
  {
    h: '18. Contact us',
    ps: [
      'If you have questions or requests regarding this policy or your personal information, contact us at hello@mygolflinks.com and we will respond as promptly as we can. MyGolfLinks, LLC is an Indiana limited liability company. This policy and any dispute relating to it are governed by the laws of the State of Indiana, United States, without regard to its conflict of laws rules.',
    ],
  },
];

export default function Privacy() {
  usePageMeta('Privacy Policy | MyGolfLinks', 'How MyGolfLinks, LLC collects, uses, and protects information on mygolflinks.com and in the MyGolfLinks app.');
  return <Legal title="Privacy Policy" effective="July 25, 2026" sections={SECTIONS} />;
}
